<template>
    <v-row justify="center">
        <v-dialog v-model="dialog" persistent max-width="900px">
            <v-card>
                <v-alert v-if="error" tile type="error">{{ error }}</v-alert>
                <v-card-title>
                    <b class="mr-1">{{ selectedDepartmentNode ? selectedDepartmentNode.name : "" }}</b>
                    : Add sections
                    <v-spacer />

                    <v-btn tile text outlined class="elevation-0" @click="alignSections('V')">
                        <v-icon class="grey--text text--darken-3">view_stream</v-icon>
                    </v-btn>
                    <v-btn tile text outlined class="elevation-0" @click="alignSections('H')">
                        <v-icon class="grey--text text--darken-3">view_week</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pb-0">
                    <v-row class="justify-space-between">
                        <v-col cols="4" md="3" class="pb-0">
                            <v-text-field
                                dense
                                v-model="width"
                                :rules="[rules.required]"
                                label="Width*"
                                type="number"
                                suffix="m"
                                outlined
                                class="subtitle-2"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" md="3" class="pb-0">
                            <v-text-field
                                dense
                                v-model="height"
                                :rules="[rules.required]"
                                label="Height*"
                                type="number"
                                suffix="m"
                                outlined
                                class="subtitle-2"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" md="3" class="pt-1">
                            <v-select
                                dense
                                v-model="numSections"
                                :items="numSectionsChoices"
                                label="# Sections"
                                outlined
                                :menu-props="{offsetY:true}"
                                class="subtitle-2 mt-2"
                            ></v-select>
                        </v-col>
                        <v-col cols="4" md="3" class="pb-0">
                            <v-text-field
                                dense
                                v-model="gap"
                                :rules="[rules.required]"
                                label="Gap*"
                                type="number"
                                suffix="m"
                                outlined
                                :disabled="isGapDisabled"
                                class="subtitle-2"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-card-text class="pt-0 font-weight-medium">
                            <v-row align="center">
                                <v-divider></v-divider>
                                <span class="pl-3 pr-3">Sections</span>
                                <v-divider></v-divider>
                            </v-row>
                        </v-card-text>
                    </v-row>
                </v-card-text>

                <v-card-text class="pt-3">
                    <v-form ref="form">
                        <v-row
                            v-for="(section, index) in editedItem.sections"
                            v-bind:key="index"
                            align="center"
                            dense
                        >
                            <v-col cols="12" md="4">
                                <v-text-field
                                    dense
                                    v-model="section.name"
                                    :rules="[rules.required]"
                                    label="Name*"
                                    :error-messages="section.nameError"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="6" md="3">
                                <v-text-field
                                    dense
                                    v-model="section.layoutY"
                                    :rules="[rules.required]"
                                    label="Top*"
                                    type="number"
                                    suffix="m"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="6" md="3">
                                <v-text-field
                                    dense
                                    v-model="section.layoutX"
                                    :rules="[rules.required]"
                                    label="Left*"
                                    type="number"
                                    suffix="m"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="12" md="2">
                                <v-switch
                                    v-model="section.isPlaceholder"
                                    class="mt-0"
                                    label="Placeholder"
                                ></v-switch>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close">Cancel</v-btn>
                    <v-btn outlined rounded :loading="isSaving" color="primary" @click="save">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import helper from "@/utils/helper.js"
import Config from "@/utils/config.json";

export default {
    name: "greenhouse-department-sections-add-dialog",
    props: ["dialog", "selectedDepartmentNode", "greenhouseDepartmentName"],
    components: {},
    data() {
        return {
            error: null,
            isSaving: false,
            numSectionsChoices: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
            numSections: 1,
            gap: 0,
            // alignDirection: null,
            width: 10,
            height: 10,
            editedItem: {
                sections: [
                    {
                        name: null,
                        isPlaceholder: false,
                        layoutX: 0,
                        layoutY: 0,
                        nameError: null
                    }
                ]
            },
            defaultItem: {
                sections: [
                    {
                        name: null,
                        isPlaceholder: false,
                        layoutX: 0,
                        layoutY: 0,
                        nameError: null
                    }
                ]
            },
            rules: {
                required: v =>
                    !helper.isEmpty(v) || "This field is required",
                minLength: len => v =>
                    (v || "").length >= len ||
                    `Invalid character length, required ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            },
            greenhouseDialog: false
        };
    },
    computed: {
        meterToPixelRatio() {
            return Config.meterToPixelRatio;
        }, // 1 meter = 10 pixels

        isGapDisabled() {
            return this.numSections <= 1;
        }
    },
    watch: {
        // reset form when dialog open or close
        dialog() {
            this.resetForm();
        },
        numSections(val) {
            if (val > this.editedItem.sections.length) {
                for (
                    var i = this.editedItem.sections.length + 1;
                    i <= val;
                    i++
                ) {
                    this.editedItem.sections.push({
                        name: "",
                        isPlaceholder: false,
                        layoutX: 0,
                        layoutY: 0,
                        nameError: null
                    });
                }
            } else if (val < this.editedItem.sections.length) {
                this.editedItem.sections.splice(
                    val,
                    this.editedItem.sections.length - val
                );
            }
        }
        // alignDirection() {
        //     this.alignSections();
        // }
    },
    methods: {
        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            this.width = 1;
            this.height = 1;
            this.gap = 0;
            this.numSections = 1;

            this.editedItem = Object.assign({}, this.defaultItem);
        },

        alignSections(direction) {
            const startX = +this.editedItem.sections[0].layoutX;
            const startY = +this.editedItem.sections[0].layoutY;

            this.editedItem.sections.forEach((item, index) => {
                if (direction === "H") {
                    item.layoutX = parseFloat(
                        (startX + (+this.width + +this.gap) * index).toFixed(2)
                    );
                    item.layoutY = parseFloat(+startY.toFixed(2));
                }

                if (direction === "V") {
                    item.layoutX = parseFloat(+startX.toFixed(2));
                    item.layoutY = parseFloat(
                        (startY + (+this.height + +this.gap) * index).toFixed(2)
                    );
                }
            });
        },

        close() {
            this.$emit("update:dialog", false);
            setTimeout(() => {
                this.resetForm();
            }, 1000);
        },

        clearNameError() {
            this.editedItem.sections.forEach(item => {
                item.nameError = null;
            });
        },

        validateSectionNames() {
            var errorCount = 0;

            this.editedItem.sections.forEach((item, index) => {
                if (item.name) {
                    // check if duplicate in existing sections
                    var e_index = this.selectedDepartmentNode.greenhouseDepartmentSectionSet.edges.findIndex(
                        element => {
                            return (
                                element.node.name.toLowerCase() ===
                                item.name.toLowerCase()
                            );
                        }
                    );

                    if (e_index > -1) {
                        item.nameError = "The name already exist";
                        errorCount++;
                    } else {
                        // check if duplicate in new sections
                        var n_index = this.editedItem.sections
                            .slice(0, index)
                            .findIndex(element => {
                                return (
                                    element.name.toLowerCase() ===
                                    item.name.toLowerCase()
                                );
                            });

                        if (n_index > -1) {
                            item.nameError = "Duplicate name";
                            errorCount++;
                        }
                    }
                }
            });

            return errorCount;
        },

        save() {
            // clear old error
            this.clearNameError();

            // check if form valid
            if (!this.$refs.form.validate()) {
                return;
            }

            // check duplicate name
            var errorCount = this.validateSectionNames();
            if (errorCount > 0) {
                return;
            }

            // set form state
            this.error = null;
            // this.isSaving = true;

            const newSections = this.editedItem.sections.map(item => {
                return {
                    name: item.name,
                    isPlaceholder: item.isPlaceholder,
                    layoutX: item.layoutX * this.meterToPixelRatio,
                    layoutY: item.layoutY * this.meterToPixelRatio,
                    width: this.width * this.meterToPixelRatio,
                    height: this.height * this.meterToPixelRatio
                };
            });

            this.$emit("item-created", newSections);
            this.close();
        }
    }
};
</script>